import type React from 'react'
import { useState, useCallback, useRef, useEffect } from 'react'

import Slider from 'rc-slider'
import { useIntl, FormattedMessage } from 'react-intl'
import { NumericFormat } from 'react-number-format'
import { styled } from 'styled-components'
import 'rc-slider/assets/index.css'

import { InputBordered } from '@b-stock/fe-core/bstock-react'
import { Colors, Shadows, Typography } from '@b-stock/fe-core/design-system'

import type {
  Coordinates,
  DistanceRange,
} from '@/components/listings/search/SearchFilters/shared/commonTypes'
import toDisplayNames from '@/components/listings/search/SearchFilters/toDisplayNames'
import getAddressDetails from '@/helpers/api/getAddressDetails'

import { useTrackFilterUsage } from '../../shared/analytics'
import { createDistanceRangeAppliedComponent } from '../../shared/filterUtils'
import WithDynamicButton from '../../shared/WithDynamicApplyButton'
import type { ListingFilter, FilterProps } from '../../types'

type DistanceInputProps = {
  value: number | null
  label: React.ReactNode
  error?: string
  suffix?: React.ReactNode
  minValue: number
  maxValue: number
  step?: number
  className?: string
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
}

const RangeContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 0.75rem;
`

const StyledRange = styled(Slider)`
  height: 1.25rem;
  padding: 0.5rem 0;

  .rc-slider-rail {
    background: ${Colors.Semantic.Neutral.medium_grey};
    height: 0.25rem;
    margin-bottom: -0.25rem;
    border-radius: 6.25rem;
  }

  .rc-slider-track {
    background: ${Colors.Semantic.BStock.onhover};
    height: 0.25rem;
    margin-bottom: -0.25rem;
    border-radius: 6.25rem;
  }

  .rc-slider-handle {
    background: ${Colors.Semantic.Neutral.white};
    border: 0.0625rem solid ${Colors.Semantic.BStock.default.lightBackground};
    ${Shadows.Popovers}
    height: 1.25rem;
    width: 1.25rem;
    opacity: 1;
    margin-top: -0.5rem;
  }

  &&& {
    .rc-slider-handle-dragging {
      background: ${Colors.Semantic.BStock.onclick.lightBackground};
      border-color: ${Colors.Semantic.BStock.onclick.lightBackground};
      ${Shadows.Popovers}
    }
  }
`

const MinMaxContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.25rem;
`

const MinMaxLabel = styled(NumericFormat)`
  ${Typography.Body4}
  color: ${Colors.Semantic.Neutral.black};
`

type ZipCodeInputProps = {
  value: string | null
  onChange: (value: string) => void
  error: boolean
  label: string
  onBlur: () => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
}

const ZipCodeInputContainer = styled.div`
  position: relative;
  width: 100%;
`

const DistanceStyledInputBordered = styled(InputBordered)`
  input {
    flex: 0 !important;
    width: 2.5rem !important;
  }
`

const ZipCodeInputComponent: React.FC<ZipCodeInputProps> = ({
  value,
  onChange,
  error,
  label,
  onBlur,
  onKeyDown,
}) => {
  return (
    <ZipCodeInputContainer>
      <InputBordered
        label={label}
        onChange={(e) => onChange(e.target.value)}
        value={value || ''}
        error={error}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        data-testid="zip-code-input"
        type="text"
      />
      {error && (
        <div data-testid="zip-code-error">
          <InputBordered.ErrorMessage>
            <FormattedMessage id="Filters.ZipCodeInput.hasError" />
          </InputBordered.ErrorMessage>
        </div>
      )}
    </ZipCodeInputContainer>
  )
}

/**
 * Input component for displaying and editing mile values
 */
const DistanceInput = ({
  label,
  value,
  className,
  onFocus,
  onBlur,
  onChange,
  onKeyDown,
  suffix,
  minValue,
  maxValue,
}: DistanceInputProps) => {
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const specialKeys = ['+', '-', 'e', 'E', '.']

      // Block special keys
      if (
        specialKeys.includes(event.key) ||
        (event.key === '0' && (!value || value === 0))
      ) {
        event.preventDefault()
        return
      }

      // Call parent onKeyDown handler for Enter key
      if (event.key === 'Enter') {
        onKeyDown?.(event)
      }
    },
    [value, onKeyDown]
  )

  return (
    <DistanceStyledInputBordered
      className={className}
      label={label}
      value={value?.toString() ?? ''}
      onChange={onChange}
      onFocus={onFocus}
      onBlur={onBlur}
      suffix={(!value || value >= maxValue) && suffix ? suffix : undefined}
      inputMode="numeric"
      onKeyDown={handleKeyDown}
      min={minValue}
      max={maxValue}
      type="text"
      aria-label="Enter distance"
      data-testid="distance-input"
    />
  )
}

/**
 * Filter component that allows users to specify a distance range with a slider and input field
 */

const minDistance = 1
export const maxDistance = 2000
export const defaultDistance = 1000

const formatZipCode = (value: string) => {
  const digits = value.replace(/\D/g, '')

  if (digits.length === 9) {
    return `${digits.slice(0, 5)}-${digits.slice(5)}`
  }
  if (digits.length === 5) {
    return digits
  }
  return null
}

const getAddressCoordinates = async (zipCode: string) => {
  return getAddressDetails(zipCode)
}

const DistanceRangeFilter: ListingFilter<DistanceRange> = ({
  desktopScreen,
  onChange,
  value,
  onError,
}: FilterProps<DistanceRange>) => {
  const intl = useIntl()
  const trackFilterUsage = useTrackFilterUsage<{
    zipcode: string | null
    miles: number | null
  }>('distance_filter_alp')
  const [distance, setDistance] = useState<number | null>(
    value.distance ?? defaultDistance
  )
  const [zipCode, setZipCode] = useState<string | null>(value.zipCode ?? null)
  const [zipCodeError, setZipCodeError] = useState<boolean>(false)
  const [showResults, setShowResults] = useState(false)
  const coordinates = useRef<Coordinates | null>(value.coordinates ?? null)
  const [showZipError, setShowZipError] = useState(false)

  const sliderRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setDistance(value.distance ?? defaultDistance) // oxlint-disable-line react/set-state-in-effect
  }, [value.distance])

  useEffect(() => {
    // Only sync zipCode from props if it's being explicitly set (not null)
    // This prevents clearing the zipCode when distance reaches max
    if (value.zipCode !== null) {
      setZipCode(value.zipCode) // oxlint-disable-line react/set-state-in-effect
      coordinates.current = value.coordinates ?? null
    } else if (value.distance === null) {
      // Only clear zipCode if ALL values are null (full reset)
      // When zipCode is null and distance is null, it's a full reset
      setZipCode(null)
      coordinates.current = null
    }
  }, [value.zipCode, value.coordinates, value.distance])

  const handleSliderChange = useCallback(
    (value: number | number[]) => {
      // rc-slider may pass array or single value; extract first element if array
      const singleValue = Array.isArray(value) ? (value[0] ?? null) : value
      setDistance(singleValue)

      if (!zipCode) {
        setZipCodeError(false)
        return
      }
    },
    [zipCode]
  )

  const handleInputChange = useCallback(
    (value: string) => {
      if (value === '') {
        setDistance(null)
        return
      }

      const parsedValue = parseInt(value)
      if (isNaN(parsedValue)) {
        return
      }

      const validValue = Math.abs(parsedValue)
      setDistance(validValue)
      if (!desktopScreen) {
        // Always send the distance value, even if it's at max
        onChange({
          distance: validValue,
          zipCode: zipCode,
          coordinates: coordinates.current,
        })
      }
      if (zipCode && validValue < maxDistance) {
        setShowResults(true)
      } else {
        setShowResults(false)
      }
    },
    [desktopScreen, onChange, zipCode]
  )

  const handleBlur = useCallback(() => {
    if (!distance || distance >= maxDistance || distance < minDistance) {
      setDistance(defaultDistance)
      setShowResults(false)
      // Reset to default distance when invalid
      if (zipCode && coordinates.current) {
        onChange({
          distance: defaultDistance,
          zipCode: zipCode,
          coordinates: coordinates.current,
        })
      }
    }
    if (distance && distance < maxDistance && (!zipCode || zipCode === '')) {
      setZipCodeError(true)
      setShowZipError(true)
    } else {
      setZipCodeError(false)
      setShowZipError(false)
    }
  }, [distance, onChange, zipCode])

  const handleZipCodeChange = useCallback(
    async (value: string) => {
      setShowResults(false)
      coordinates.current = null

      if (value === '') {
        setZipCode(null)
        setZipCodeError(false)
        setShowZipError(false)
        // Explicitly reset filter when user clears the zipcode
        onChange({
          distance: null,
          zipCode: null,
          coordinates: null,
        })
        return
      }

      setZipCode(value)
      setShowZipError(false)

      if (distance && distance < maxDistance) {
        setShowResults(true)
      } else {
        setShowResults(false)
      }

      const formatted = formatZipCode(value)
      if (!formatted) {
        setZipCodeError(true)
        if (!desktopScreen) {
          onError?.(true)
          setShowZipError(true)
        }
        return
      }

      const addressCoordinates = await getAddressCoordinates(formatted)
      if (!addressCoordinates) {
        setZipCodeError(true)
        if (!desktopScreen) {
          onError?.(true)
          setShowZipError(true)
        }
        return
      }

      coordinates.current = {
        lat: addressCoordinates.lat,
        long: addressCoordinates.long,
      }
      setZipCodeError(false)
      onError?.(false)

      const hasValidDistance = distance && distance < maxDistance

      if (hasValidDistance) {
        if (!desktopScreen) {
          onChange({
            distance,
            zipCode: formatted,
            coordinates: coordinates.current,
          })
        }
      }
    },
    [distance, desktopScreen, onChange, onError]
  )

  const handleZipCodeBlur = useCallback(() => {
    if (zipCode) {
      const formatted = formatZipCode(zipCode)
      if (formatted) {
        setZipCode(formatted)
      } else {
        coordinates.current = null
        setZipCodeError(true)
      }
    }
  }, [zipCode])

  const handleSliderComplete = useCallback(() => {
    trackFilterUsage({
      zipcode: zipCode,
      miles: distance,
    })

    if (!zipCode || !coordinates.current) {
      if (distance && distance < maxDistance) {
        setZipCodeError(true)
        setShowZipError(true)
        if (!desktopScreen) {
          onError?.(true)
        }
      } else {
        setZipCodeError(false)
        setShowZipError(false)
        if (!desktopScreen) {
          onError?.(false)
        }
      }
      return
    }

    const formattedZipCode = formatZipCode(zipCode)
    if (!formattedZipCode) {
      setZipCodeError(true)
      if (!desktopScreen) {
        setShowZipError(true)
        onError?.(true)
      }
      return
    }

    // Keep the actual distance value even if it's at max - don't convert to null
    // This prevents external filter logic from clearing the zipcode
    onChange({
      distance: distance ?? maxDistance,
      zipCode: formattedZipCode,
      coordinates: coordinates.current,
    })

    setShowResults(false)
  }, [distance, trackFilterUsage, zipCode, desktopScreen, onError, onChange])

  const handleZipCodeKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        // Trigger search with current distance (default 1000 if not changed)
        if (zipCode && coordinates.current && distance) {
          handleSliderComplete()
        }
      }
    },
    [zipCode, distance, handleSliderComplete]
  )

  const handleDistanceKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        // Trigger search when Enter is pressed in distance field
        if (zipCode && coordinates.current && distance) {
          handleSliderComplete()
        }
      }
    },
    [zipCode, distance, handleSliderComplete]
  )

  const handleShowResults = useCallback(() => {
    handleSliderComplete()
    handleSliderChange(distance ?? 0)
    trackFilterUsage({
      zipcode: zipCode,
      miles: distance,
    })
  }, [
    distance,
    handleSliderChange,
    handleSliderComplete,
    trackFilterUsage,
    zipCode,
  ])

  return (
    <WithDynamicButton
      buttonContent={intl.formatMessage({
        id: 'Filters.rangeDistance.filterButton',
      })}
      isButtonVisible={desktopScreen && showResults}
      onClick={handleShowResults}
    >
      <ZipCodeInputComponent
        label={intl.formatMessage({
          id: 'Filters.rangeDistance.zipPlaceholder',
        })}
        onChange={handleZipCodeChange}
        onBlur={handleZipCodeBlur}
        onKeyDown={handleZipCodeKeyDown}
        value={zipCode}
        error={showZipError && zipCodeError}
      />
      <RangeContainer data-testid="distance-slider" ref={sliderRef}>
        <StyledRange
          min={minDistance}
          max={maxDistance}
          step={1}
          value={distance ?? 0}
          onChange={handleSliderChange}
          onChangeComplete={handleSliderComplete}
        />
      </RangeContainer>
      <MinMaxContainer>
        <MinMaxLabel
          displayType="text"
          thousandSeparator
          value={1}
          data-testid="min"
        />
        <MinMaxLabel
          data-testid="max"
          displayType="text"
          thousandSeparator
          suffix={`+ ${intl.formatMessage({ id: 'Filters.rangeDistance.unit' })}`}
          value={maxDistance}
        />
      </MinMaxContainer>
      <DistanceInput
        label={intl.formatMessage({ id: 'Filters.rangeDistance.label' })}
        value={distance}
        onChange={(e) => handleInputChange(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleDistanceKeyDown}
        suffix="+"
        className="distance-input"
        minValue={minDistance}
        maxValue={maxDistance}
      />
    </WithDynamicButton>
  )
}

DistanceRangeFilter.expandedOnInit = true
DistanceRangeFilter.getOwnProps = ({ state, updateFilters }) => ({
  filterKey: 'distanceRange',
  onChange: (value: DistanceRange) => {
    // Don't reset zipCode/coordinates when distance is null or >= maxDistance
    // Only reset if zipCode itself is null
    if (!value.zipCode && !value.coordinates) {
      updateFilters({
        distanceRange: { distance: null, zipCode: null, coordinates: null },
      })
    } else {
      updateFilters({ distanceRange: value })
    }
  },
  value: state.filters.distanceRange,
})

DistanceRangeFilter.label = 'Filters.distance'

DistanceRangeFilter.Applied = createDistanceRangeAppliedComponent(
  'distanceRange',
  DistanceRangeFilter.label,
  toDisplayNames.distanceRange
)

export default DistanceRangeFilter
